// src/pages/Accounting/Reports/AccountList/components/ScheduleReportModal.tsx

import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, X } from 'lucide-react';
import { SCHEDULE_FREQUENCIES } from '../constants';
import type { ScheduledReport } from '../types';

interface ScheduleReportModalProps {
  open: boolean;
  onClose: () => void;
  onSchedule: (input: Omit<ScheduledReport, 'id' | 'active'>) => void;
  reportName: string;
  defaultTimezone: string;
}

const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export default function ScheduleReportModal({ open, onClose, onSchedule, reportName, defaultTimezone }: ScheduleReportModalProps) {
  const [frequency, setFrequency] = useState<ScheduledReport['frequency']>('weekly');
  const [dayOfWeek, setDayOfWeek] = useState('Monday');
  const [time, setTime] = useState('09:00');
  const [recipients, setRecipients] = useState('');
  const [format, setFormat] = useState<ScheduledReport['format']>('pdf');

  useEffect(() => {
    if (open) {
      setFrequency('weekly');
      setDayOfWeek('Monday');
      setTime('09:00');
      setRecipients('');
      setFormat('pdf');
    }
  }, [open]);

  if (!open) return null;

  const handleSchedule = () => {
    onSchedule({
      frequency,
      dayOfWeek: frequency === 'weekly' ? dayOfWeek : undefined,
      time,
      timezone: defaultTimezone,
      recipients: recipients.split(',').map((r) => r.trim()).filter(Boolean),
      format,
    });
  };

  return createPortal(
    <div onClick={onClose} className="fixed inset-0 z-[1000] bg-black/40 flex items-center justify-center p-4">
      <div onClick={(e) => e.stopPropagation()} className="bg-white border border-line rounded-xl shadow-2xl p-6 w-full max-w-[520px]">
        <div className="relative mb-1">
          <h4 className="text-[17px] font-extrabold text-ink text-center m-0">Schedule {reportName}</h4>
          <button type="button" onClick={onClose} aria-label="Close" className="absolute right-0 top-0 text-ink3 cursor-pointer border-none bg-transparent hover:text-ink">
            <X size={18} />
          </button>
        </div>
        <p className="text-[12.5px] text-ink3 text-center m-0 mb-4">
          Send this report automatically on a schedule you choose.
        </p>

        <label className="block text-[11.5px] text-ink3 font-semibold mb-1.5">Frequency</label>
        <div className="grid grid-cols-4 gap-2 mb-3">
          {SCHEDULE_FREQUENCIES.map((f) => (
            <button
              key={f.value}
              type="button"
              onClick={() => setFrequency(f.value)}
              className={`p-2.5 rounded-md text-[12px] font-semibold border cursor-pointer transition ${
                frequency === f.value ? 'border-teal-dark bg-[#F4FAF7] text-teal-dark' : 'border-line bg-white text-ink hover:bg-[#FAFBFB]'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {frequency === 'weekly' && (
          <>
            <label className="block text-[11.5px] text-ink3 font-semibold mb-1.5">Day of week</label>
            <div className="relative mb-3">
              <select value={dayOfWeek} onChange={(e) => setDayOfWeek(e.target.value)} className="w-full bg-white border border-line rounded-md px-2.5 py-2 text-[12.5px] text-ink outline-none focus:border-teal-dark appearance-none cursor-pointer pr-8">
                {DAYS_OF_WEEK.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
              <ChevronDown size={12} strokeWidth={2.5} className="text-ink3 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
            </div>
          </>
        )}

        <label className="block text-[11.5px] text-ink3 font-semibold mb-1.5">Time ({defaultTimezone})</label>
        <input type="time" value={time} onChange={(e) => setTime(e.target.value)} className="w-full bg-white border border-line rounded-md px-2.5 py-2 text-[12.5px] text-ink outline-none focus:border-teal-dark mb-3" />

        <label className="block text-[11.5px] text-ink3 font-semibold mb-1.5">Send to (comma separated)</label>
        <input value={recipients} onChange={(e) => setRecipients(e.target.value)} placeholder="your@email.com" className="w-full bg-white border border-line rounded-md px-2.5 py-2 text-[12.5px] text-ink placeholder:text-ink4 outline-none focus:border-teal-dark mb-3" />

        <label className="block text-[11.5px] text-ink3 font-semibold mb-1.5">Format</label>
        <div className="relative mb-5">
          <select value={format} onChange={(e) => setFormat(e.target.value as ScheduledReport['format'])} className="w-full bg-white border border-line rounded-md px-2.5 py-2 text-[12.5px] text-ink outline-none focus:border-teal-dark appearance-none cursor-pointer pr-8">
            <option value="pdf">PDF</option>
            <option value="xlsx">Excel</option>
            <option value="csv">CSV</option>
          </select>
          <ChevronDown size={12} strokeWidth={2.5} className="text-ink3 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
        </div>

        <div className="flex justify-end gap-2 pt-3 border-t border-lineSoft">
          <button type="button" onClick={onClose} className="text-ink3 text-[13px] font-bold px-4 py-2 border-none bg-transparent cursor-pointer hover:text-ink">
            Cancel
          </button>
          <button
            type="button"
            disabled={recipients.trim().length === 0}
            onClick={handleSchedule}
            className="bg-teal-dark text-white text-[13px] font-bold px-5 py-2 rounded-md border-none cursor-pointer hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Schedule
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}